<script lang="ts">
    import cn from 'classnames';
    import { configService } from '$lib/services';
    import { sendCallEvent } from '$lib/services/gtm/methods';

    import { changeCallMuteSettings } from '@api/room/call';
    import { updatePreferredLang } from '@api/user';
    import { streamingDebuggerStore } from '@components/room/streaming-debugger/stores/debugger';
    import { Container } from '@components/ui/container';
    import { Dialog } from '@components/ui/dialog';
    import { Dropdown, DropdownItem } from '@components/ui/dropdown';
    // eslint-disable-next-line no-restricted-imports
    import { isOperator, userStore } from '@components/user/store';
    import { LL, locale } from '@i18n/i18n-svelte';
    import type { Locales } from '@i18n/i18n-types';
    import Phone from '@images/icons/call.svg';
    import Warning from '@images/icons/warning.svg';
    import { Cookie } from '@shared/cookies';
    import { getHref } from '@shared/routing';
    import { SupportedLanguage, UserType } from '@shared/types';
    import { Font, FontTypes } from '@uikit/font';
    import { Switch } from '@uikit/form/switch';
    import { Link } from '@uikit/link';
    import { IconName, SvgIcon } from '@uikit/svg-icon';
    import { setCookie } from '@utils/cookies';

    import { FLAGS, NAMES } from './constants';

    import s from './settings-dropdown.module.css';

    import { page } from '$app/stores';

    let isDropdownOpened = false;

    $: user = $userStore.user;

    const onChangeCallDisabled = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        sendCallEvent({
            event: 'call_not_disturb_click',
            type: $isOperator ? UserType.Model : UserType.User,
            status: target.checked,
        });
        await changeCallMuteSettings(!target.checked);
        userStore.changeDisableSettings(!target.checked);
    };

    async function selectLocale(e: Event, l: Locales) {
        e.preventDefault();
        e.stopPropagation();
        setCookie(Cookie.Lang, l);
        if (user) {
            await updatePreferredLang();
        }
        isOpenLanguage = false;
        window.location.href = getHref($page.url, l as SupportedLanguage);
    }

    let isOpenLanguage = false;

    const LANGUAGES: Locales[] = Object.keys(NAMES) as Locales[];

    function getHreflangLocale(url: URL, l: Locales) {
        return getHref(url, l as SupportedLanguage);
    }

    $: if (isDropdownOpened) {
        sendCallEvent({
            event: 'call_not_disturb_show',
            type: $isOperator ? UserType.Model : UserType.User,
        });
    }

</script>

{#if isOpenLanguage || !user}
    <Container
        classes={cn({ [s.seoHidden]: !isOpenLanguage })}
        closeHandler={() => isOpenLanguage = false }
        testId='languageModal'
    >
        <Dialog classes={s.selectLanguageDialog}>
            <Font
                    tagName={FontTypes.TagName.h2}
                    type={FontTypes.Type.h3}
                    className={s.selectLanguageDialogTitle}
                    attrs={{ 'data-testid': 'title' }}
                    isBold
            >
                {$LL.locale.dialogTitle()}
            </Font>
            <ul class={s.dialogItems}>
                {#each LANGUAGES as key}
                    <Font tagName={FontTypes.TagName.li}>
                        <Link classes={s.dialogItem}
                              offVisited
                              href={getHreflangLocale($page.url, key)}
                              attrs={{ hreflang: key, 'data-testid': `lg-${key}` }}
                              on:click={(e) => selectLocale(e, key)}
                        >
                            <div class={s.dialogItemIcon} style="background-image: url({`'${FLAGS[key]}'`})">
                            </div>
                            <div>
                                {NAMES[key]}
                            </div>
                        </Link>
                    </Font>
                {/each}
            </ul>
        </Dialog>
    </Container>
{/if}

{#if user}
    <Dropdown bind:isOpen={isDropdownOpened} testId='settingsMenu'>
        <svelte:fragment slot="trigger">
            <SvgIcon classes={s.dropdownTrigger} width={24} icon={IconName.Settings}
                     attrs={{ 'data-testid': 'settingsIcon' }}/>
        </svelte:fragment>
        <svelte:fragment slot="dropdown">
            <div class={s.buttonsWrapperAuth}>
                <DropdownItem withHover={false} on:click={() => isOpenLanguage = true }>
                    <div class={cn(s.profileDropdownItem, s.locale)} data-testid="language">
                        <img alt={NAMES[$locale]} width="16px" height="12px" src={`${FLAGS[$locale]}`}/>
                        {$LL.locale.choose({ locale: NAMES[$locale] })}
                    </div>
                </DropdownItem>
                {#if user && configService.get('callsAvailable')}
                    <DropdownItem withHover={false}>
                        <div
                                class={cn(s.profileDropdownItem, s.callItem)}
                                data-close="no"
                                data-testid="incomingCalls"
                        >
                            <div class={s.callTitleWrapper}>
                                <div class={s.callTitle}>
                                    <img src={Phone} alt="call"/>
                                    {$LL.call.dropdownTitle()}
                                </div>
                                <Switch
                                        name="call-disalbed"
                                        classes={s.callSwitch}
                                        checked={!user?.callsDisabled}
                                        on:change={onChangeCallDisabled}
                                />
                            </div>
                            <div class={s.callSubTitle}>
                                <Font
                                        type={FontTypes.Type.caption}
                                        tagName={FontTypes.TagName.div}
                                        color={FontTypes.Color.Secondary}
                                >
                                    {$LL.call.dropdownSubtitle()}
                                </Font>
                            </div>
                        </div>
                    </DropdownItem>
                    {#if $isOperator}
                        <DropdownItem
                                withHover={false}
                                on:click={() => { streamingDebuggerStore.set(!$streamingDebuggerStore);} }
                        >
                            <div class={s.profileDropdownItem}>
                                <img src={Warning} alt="warning"/>
                                Streaming debug console
                            </div>
                        </DropdownItem>
                    {/if}
                {/if}
            </div>
        </svelte:fragment>
    </Dropdown>
{:else}
    <div class={s.landingItem}
         on:click={() => isOpenLanguage = true}
         style="background-image: url({`'${FLAGS[$locale]}'`})"
         data-testid="language"
    >
    </div>
{/if}
